/**
 * KAN-885: период таблицы CRM по дате взятия в работу и исход таблицы.
 * KAN-883: период выбирается в CrmPeriodControl (быстрые кнопки и календарь).
 * Адрес: без параметров - сегодня по Москве; ?from=&to= - период (границы
 * разбирает parseCrmRange); ?period=all - всё время, без дат. Исход ?outcome=
 * пишется в адрес, только если отличается от исхода по умолчанию 'all'
 * (KAN-887: и при "Всё время"; раньше там был 'open').
 * KAN-887/894/898: "Продажи", "Мусор" и другие кнопки быстрого выбора - lib/crmTableShortcuts.
 * KAN-910: быстрый период именем ?period=today|yesterday|7d|30d|all (lib/urlPeriod), даты
 * ?from=&to= только из календаря; без параметров - сегодня, и умолчание пишется в адрес.
 * Группировка строк по дню для заголовков дней: KAN-973 - по period_at (взятие в периоде
 * или появление лида), без period_at - по taken_at; дни по Москве.
 */
import type { CrmTableOutcome } from '@/types/crmPipeline';
import { formatCrmDayTitle } from './crmBoard';
import { CRM_OUTCOME_OPTIONS } from './crmPipeline';
import type { PeriodChoice } from './periodPresets';
import { todayMoscow, type CrmRange, type CrmTableRow } from './crmTable';
import { parseUrlPeriod, PERIOD_URL_NAME, type UrlPeriodKeys } from './urlPeriod';

export const CRM_PERIOD_PARAM = { from: 'from', to: 'to', period: 'period', outcome: 'outcome' } as const;
/** Значение ?period= для "Всё время". */
export const CRM_PERIOD_ALL = 'all';

/** Быстрые кнопки периода таблицы. */
export const CRM_TABLE_PERIOD_CHOICES: readonly PeriodChoice[] = ['today', 'yesterday', 'week', 'all'];

/** Исход таблицы: "Все" и исходы доски (Открытые, Успех, Отказ). */
export const CRM_TABLE_OUTCOME_OPTIONS: readonly { value: CrmTableOutcome; label: string }[] = [
  { value: 'all', label: 'Все' },
  ...CRM_OUTCOME_OPTIONS,
];

/** Период таблицы: всё время (range null) или период по Москве. */
export interface CrmTablePeriod {
  range: CrmRange | null;
}

/** KAN-910: параметры периода таблицы (те же у "Лиды по дням"). */
export const CRM_TABLE_PERIOD_KEYS: UrlPeriodKeys = {
  period: CRM_PERIOD_PARAM.period,
  from: CRM_PERIOD_PARAM.from,
  to: CRM_PERIOD_PARAM.to,
};

/** Период таблицы по умолчанию (имя в адресе). */
export const CRM_TABLE_DEFAULT_PERIOD = PERIOD_URL_NAME.today;

/** Период из адреса (без параметров - сегодня; имя главнее дат). */
export function parseCrmTablePeriod(params: URLSearchParams, now: Date = new Date()): CrmTablePeriod {
  return { range: parseUrlPeriod(params, CRM_TABLE_PERIOD_KEYS, 'today', { now }).range };
}

/** Адрес с периодом: range - ?from=&to= (period убирается), null - ?period=all (from/to убираются). */
export function withCrmTablePeriod(prev: URLSearchParams, range: CrmRange | null): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (range) {
    next.set(CRM_PERIOD_PARAM.from, range.from);
    next.set(CRM_PERIOD_PARAM.to, range.to);
    next.delete(CRM_PERIOD_PARAM.period);
  } else {
    next.delete(CRM_PERIOD_PARAM.from);
    next.delete(CRM_PERIOD_PARAM.to);
    next.set(CRM_PERIOD_PARAM.period, CRM_PERIOD_ALL);
  }
  return next;
}

/** Исход по умолчанию: всегда 'all' (KAN-887), range оставлен для единого контракта. */
export function defaultCrmTableOutcome(_range: CrmRange | null): CrmTableOutcome {
  return 'all';
}

/** Исход из адреса; неизвестный или пустой - по умолчанию для периода. */
export function parseCrmTableOutcome(value: string | null, range: CrmRange | null): CrmTableOutcome {
  const known = CRM_TABLE_OUTCOME_OPTIONS.find((o) => o.value === value);
  return known ? known.value : defaultCrmTableOutcome(range);
}

/** Адрес с исходом: исход по умолчанию убирает ?outcome=. */
export function withCrmTableOutcome(
  prev: URLSearchParams,
  outcome: CrmTableOutcome,
  range: CrmRange | null
): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (outcome === defaultCrmTableOutcome(range)) next.delete(CRM_PERIOD_PARAM.outcome);
  else next.set(CRM_PERIOD_PARAM.outcome, outcome);
  return next;
}

/** Группа строк одного дня периода: day null - строки без period_at и taken_at. */
export interface CrmDayGroup {
  day: string | null;
  title: string;
  items: CrmTableRow[];
}

export const NO_TAKEN_DAY_TITLE = 'Без даты взятия';

/** KAN-973: день строки в периоде по Москве: period_at, без него - taken_at. */
function periodDay(item: CrmTableRow): string | null {
  const iso = item.row.period_at ?? item.row.taken_at;
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : todayMoscow(date);
}

/**
 * Строки по дню периода (по Москве, см. periodDay) в порядке ответа: соседние строки
 * одного дня в одной группе (сервер отдаёт свежие сверху). Новый лид без taken_at
 * попадает в группу своего дня по period_at (KAN-973).
 */
export function groupRowsByTakenDay(items: readonly CrmTableRow[], now: Date = new Date()): CrmDayGroup[] {
  const groups: CrmDayGroup[] = [];
  items.forEach((item) => {
    const day = periodDay(item);
    const last = groups[groups.length - 1];
    if (last && last.day === day) {
      last.items.push(item);
      return;
    }
    groups.push({ day, title: day ? formatCrmDayTitle(day, now) : NO_TAKEN_DAY_TITLE, items: [item] });
  });
  return groups;
}
